import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { APP_INITIALIZER, DOCUMENT, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import {
  GlobalLegendModule,
  HttpErrorInterceptor,
  HttpHeadersInterceptor,
  IconModule,
  NgbTooltipConfigService,
  ToastModule,
} from '@grid-ui/common';
import { environment, ENVIRONMENT_CONFIG } from '@grid-ui/environment';
import { PollingModule } from '@grid-ui/extract';
import {
  FEATURE_FLAG_RETRIES,
  FEATURE_FLAG_URL,
  FEATURE_FLAGS_CONFIG,
  FeatureFlagModule,
  featureFlagsConfig,
  FeatureFlagService,
} from '@grid-ui/feature-flag';
import { GlobalAnnouncementsModule } from '@grid-ui/global-announcements';
import { GlobalRoutingModule } from '@grid-ui/global-routing';
import { IpLoginService, IpLoginTokenInterceptor } from '@grid-ui/ip-login';
import { GlobalErrorHandler } from '@grid-ui/logging';
import { IGtmConfigProvider, UserAnalyticsModule } from '@grid-ui/user-analytics';
import { WhoAmIEffects } from '@grid-ui/whoami';
import { CommonUiI18nLookup } from '@maplecroft-ui/common-ui';
import { LoggingService } from '@maplecroft-ui/logging';
import { MAPS_BACKEND_URL, MAPS_SKIP_AUTHENTICATION, MapService } from '@maplecroft-ui/maps';
import { provideTranslate, TranslateService } from '@maplecroft-ui/translate';
import { EffectsModule } from '@ngrx/effects';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { CookieService } from 'ngx-cookie-service';
import { HorizontalButtonTabsModule } from '../app/shared-ui-components/horizontal-tabs/horizontal-tabs.module';
import { GtmConfigProviderService } from './analytics';
import { AppConfigModule, appInitializerFactory } from './app-config';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule, coreReducers } from './core';
import { ExtractManagementModule } from './extract-management';
import { GridComponent } from './grid/grid.component';
import { HomeModule } from './home';
import { PrivacySettingsModule } from './privacy-settings';
import { PageNotFoundModule } from './shared-ui-components/page-not-found';

const effectsForRoot = [WhoAmIEffects];

@NgModule({
  declarations: [AppComponent, GridComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    environment.test ? NoopAnimationsModule : BrowserAnimationsModule,
    RouterModule,
    StoreModule.forRoot(coreReducers, {
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    EffectsModule.forRoot(effectsForRoot),
    environment.production ? [] : StoreDevtoolsModule.instrument({ connectInZone: true }),
    AppConfigModule,
    CoreModule,
    HomeModule,
    PageNotFoundModule,
    GlobalAnnouncementsModule,
    GlobalRoutingModule,
    GlobalLegendModule,
    PollingModule,
    AppRoutingModule,
    ExtractManagementModule,
    UserAnalyticsModule,
    ToastModule,
    IconModule,
    HorizontalButtonTabsModule,
    FeatureFlagModule,
    PrivacySettingsModule,
  ],
  providers: [
    { provide: CookieService, useClass: CookieService },
    { provide: ENVIRONMENT_CONFIG, useValue: environment },
    {
      provide: FEATURE_FLAG_URL,
      useValue: '/fe-api/feature-flag/',
    },
    {
      provide: FEATURE_FLAG_RETRIES,
      useValue: 2,
    },
    {
      provide: FEATURE_FLAGS_CONFIG,
      useValue: featureFlagsConfig,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [LoggingService, IpLoginService, MapService, FeatureFlagService, NgbTooltipConfigService, Store, DOCUMENT, TranslateService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpHeadersInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: IpLoginTokenInterceptor,
      multi: true,
    },
    {
      provide: APP_BASE_HREF,
      useFactory: (platformLocation: PlatformLocation) => platformLocation.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    { provide: MAPS_BACKEND_URL, useValue: environment.mapsBackendBaseURL.replace(/\/maps$/, '') },
    { provide: MAPS_SKIP_AUTHENTICATION, useValue: true },
    { provide: IGtmConfigProvider, useClass: GtmConfigProviderService },
    provideHttpClient(
      withInterceptorsFromDi(),
      withXsrfConfiguration({
        cookieName: environment.csrfToken,
        headerName: 'X-CSRFToken',
      }),
    ),
    provideTranslate({
      availableLanguages: [{ isoCode: 'en', name: 'English' }],
      defaultLanguage: 'en',
      translationFilePaths: {
        ...CommonUiI18nLookup,
      },
    }),
  ],
})
export class AppModule {}
